import { Euler, Shape, Vector3 } from 'three';
import { generateUUID } from 'three/src/math/MathUtils';
import * as THREE from 'three';
import { Granit } from 'src/services/granits';
import { BucketsUrls } from 'src/services/file';
import { getBoxOfSole } from './sole.utils';

export const isVectorNul = (vector: Vector3) => {
  if (vector.x === 0 && vector.y === 0 && vector.z === 0) return true;
  return false;
};

export const getWidthMonument = (pieces: any[], customSoles: any[]) => {
  let backLeft: Vector3 | undefined = undefined;
  let backRight: Vector3 | undefined = undefined;
  let frontLeft: Vector3 | undefined = undefined;
  let frontRight: Vector3 | undefined = undefined;

  let minY: number | undefined = undefined;

  const aroundNumber = (numberToRound: number) => {
    return parseFloat(numberToRound.toFixed(5));
  };

  pieces.forEach((element) => {
    const elementGeometry = element.geometry.attributes['position'].array;
    for (let i = 0; i < elementGeometry.length; i += 3) {
      if (minY === undefined || minY > aroundNumber(elementGeometry[i + 1] + element.position.y)) {
        minY = aroundNumber(elementGeometry[i + 1] + element.position.y);
      }
    }
  });

  pieces.forEach((element) => {
    const elementGeometry = element.geometry.attributes['position'].array;
    for (let i = 0; i < elementGeometry.length; i += 3) {
      const elementX = aroundNumber(elementGeometry[i + 0] + element.position.x);
      const elementY = aroundNumber(elementGeometry[i + 1] + element.position.y);
      const elementZ = aroundNumber(elementGeometry[i + 2] + element.position.z);

      if (elementY === minY) {
        if (backLeft === undefined) backLeft = new Vector3(elementX, elementY, elementZ);
        if (backRight === undefined) backRight = new Vector3(elementX, elementY, elementZ);
        if (frontLeft === undefined) frontLeft = new Vector3(elementX, elementY, elementZ);
        if (frontRight === undefined) frontRight = new Vector3(elementX, elementY, elementZ);

        if (elementX < backLeft.x) backLeft = new Vector3(elementX, minY, backLeft.z);
        if (elementX < frontLeft.x) frontLeft = new Vector3(elementX, minY, frontLeft.z);
        if (elementX > backRight.x) backRight = new Vector3(elementX, minY, backRight.z);
        if (elementX > frontRight.x) frontRight = new Vector3(elementX, minY, frontRight.z);

        if (elementZ < backLeft.z) backLeft = new Vector3(backLeft.x, minY, elementZ);
        if (elementZ > frontLeft.z) frontLeft = new Vector3(frontLeft.x, minY, elementZ);
        if (elementZ < backRight.z) backRight = new Vector3(backRight.x, minY, elementZ);
        if (elementZ > frontRight.z) frontRight = new Vector3(frontRight.x, minY, elementZ);
      }
    }
  });

  for (const el of customSoles) {
    if (el.shape) {
      const { box } = getBoxOfPlating(el);

      if (!minY || box.min.y < minY) {
        minY = box.min.y;
      }
    }
  }

  for (const el of customSoles) {
    if (el.shape) {
      const { geometry } = getBoxOfSole(el);

      for (let i = 0; i < geometry.length; i += 3) {
        const elementX = aroundNumber(geometry[i + 0]);
        const elementY = aroundNumber(geometry[i + 1]);
        const elementZ = aroundNumber(geometry[i + 2]);

        if (parseFloat(elementY.toFixed(2)) === parseFloat((minY ?? 0).toFixed(2))) {
          if (backLeft === undefined) backLeft = new Vector3(elementX, elementY, elementZ);
          if (backRight === undefined) backRight = new Vector3(elementX, elementY, elementZ);
          if (frontLeft === undefined) frontLeft = new Vector3(elementX, elementY, elementZ);
          if (frontRight === undefined) frontRight = new Vector3(elementX, elementY, elementZ);

          if (elementX < backLeft.x) backLeft = new Vector3(elementX, minY, backLeft.z);
          if (elementX < frontLeft.x) frontLeft = new Vector3(elementX, minY, frontLeft.z);
          if (elementX > backRight.x) backRight = new Vector3(elementX, minY, backRight.z);
          if (elementX > frontRight.x) frontRight = new Vector3(elementX, minY, frontRight.z);

          if (elementZ < backLeft.z) backLeft = new Vector3(backLeft.x, minY, elementZ);
          if (elementZ > frontLeft.z) frontLeft = new Vector3(frontLeft.x, minY, elementZ);
          if (elementZ < backRight.z) backRight = new Vector3(backRight.x, minY, elementZ);
          if (elementZ > frontRight.z) frontRight = new Vector3(frontRight.x, minY, elementZ);
        }
      }
    }
  }

  return {
    backLeft: backLeft ?? new Vector3(0, 0, 0),
    backRight: backRight ?? new Vector3(0, 0, 0),
    frontLeft: frontLeft ?? new Vector3(0, 0, 0),
    frontRight: frontRight ?? new Vector3(0, 0, 0),
  };
};

export const getLengthWithKey = (
  mode: 'closoir' | 'double closoir' | 'tete',
  key: 'front' | 'back' | 'left' | 'right',
  depth: { front: number; back: number; left: number; right: number },
  length: number,
) => {
  switch (mode) {
    case 'closoir': {
      if (key === 'front') return length;
      if (key === 'left' || key === 'right') return length - depth.front;
      if (key === 'back') return length - depth.right - depth.left;
      break;
    }
    case 'tete': {
      if (key === 'front' || key === 'back') return length;
      if (key === 'left' || key === 'right') return length - depth.front - depth.back;
      break;
    }
    case 'double closoir': {
      if (key === 'front' || key === 'back') return length - depth.right - depth.left;
      if (key === 'left' || key === 'right') return length;
      break;
    }
  }
  return 0;
};

export const getPositionWithIndex = (
  key: 'front' | 'back' | 'left' | 'right',
  depth: { front: number; back: number; left: number; right: number },
  pieces: any[],
  customSoles: any[],
  customPlatingsIsActive: { [key: string]: boolean },
  length: number,
  width: number,
  mode: 'closoir' | 'double closoir' | 'tete',
) => {
  const box = getWidthMonument(pieces, customSoles);
  const deltaX = box.backRight.x - box.backLeft.x - width;
  const deltaZ = mode === 'closoir' ? depth.back / 2 : 0;

  if (box) {
    if (key === 'front') {
      let x = box.frontLeft.x + width / 2 + deltaX / 2;
      let y = box.frontLeft.y;
      let z = box.backLeft.z + length - depth.front;
      // if (!customPlatingsIsActive['right'] && mode === 'double closoir') x = x + depth.left / 2;
      // if (!customPlatingsIsActive['left'] && mode === 'double closoir') x = x - depth.right / 2;
      return new Vector3(x, y, z);
    }
    if (key === 'back') {
      let x = box.backLeft.x + width / 2 + deltaX / 2;
      let y = box.backLeft.y;
      let z = box.backLeft.z;

      // if (!customPlatingsIsActive['right'] && mode !== 'tete') x = x + depth.left / 2;
      // if (!customPlatingsIsActive['left'] && mode !== 'tete') x = x - depth.right / 2;
      return new Vector3(x, y, z);
    }
    if (key === 'left') {
      let x = box.backLeft.x + deltaX / 2;
      let y = box.frontLeft.y;
      let z = box.backLeft.z + length / 2 - deltaZ;
      // if (!customPlatingsIsActive['front'] && mode !== 'double closoir') z = z + depth.back / 2;
      // if (!customPlatingsIsActive['back'] && mode === 'tete') z = z - depth.front / 2;

      return new Vector3(x, y, z);
    }
    if (key === 'right') {
      let x = box.backLeft.x + width - depth.right + deltaX / 2;
      let y = box.frontRight.y;
      let z = box.backRight.z + length / 2 - deltaZ;

      // if (!customPlatingsIsActive['front'] && mode !== 'double closoir') z = z + depth.back / 2;
      // if (!customPlatingsIsActive['back'] && mode === 'tete') z = z - depth.front / 2;

      return new Vector3(x, y, z);
    }
  }
};

export const changePlatingShape = (shape: Shape, length: number, front: number, back?: number) => {
  shape.moveTo(-length / 2, 0);
  shape.lineTo(length / 2, 0);
  shape.lineTo(length / 2, -(back === undefined ? front : back));
  shape.lineTo(-length / 2, -front);
};

export const createPlatings = (
  pieces: any[],
  customSoles: any[],
  customPlatings: any[],
  setCustomPlatings: (customPlatings: any[]) => void,
  customSize?: {
    length?: number;
    width?: number;
    heightFront?: number;
    heightBack?: number;
    orientation?: 'x' | 'z';
    hasSlope?: boolean;
    mode?: 'closoir' | 'double closoir' | 'tete';
    depth?: number;
  },
) => {
  const orientation = customSize?.orientation ?? 'z';
  const mode = customSize?.mode ?? 'closoir';
  const depth = customSize?.depth ?? 0.03;

  let platings: any[] = [];

  const platingsInformations = [
    {
      key: 'front',
      label: 'Devant',
      isRotated: false,
    },
    {
      key: 'back',
      label: 'Derière',
      isRotated: false,
    },
    {
      key: 'left',
      label: 'Gauche',
      isRotated: true,
    },
    {
      key: 'right',
      label: 'Droite',
      isRotated: true,
    },
  ];

  const monumentBox = getWidthMonument(pieces, customSoles);
  let monumentWidth = monumentBox.frontRight.x - monumentBox.frontLeft.x;
  let monumentLength = monumentBox.frontRight.z - monumentBox.backRight.z;

  if (customSize) {
    if (customSize.length) monumentLength = customSize.length;
    if (customSize.width) monumentWidth = customSize.width;
  }

  if (monumentLength === 0) monumentLength = 2.4;
  if (monumentWidth === 0) monumentWidth = 1.4;

  platingsInformations.forEach((el, elIndex) => {
    let customPlatingsIsActive: { [key: string]: boolean } = {};

    customPlatings.forEach((element) => {
      customPlatingsIsActive[element.key] = element.isActive;
    });

    const shape = new Shape();
    const length = getLengthWithKey(
      mode,
      el.key as 'front' | 'back' | 'left' | 'right',
      {
        // front: customPlatingsIsActive['front'] ? depth : 0,
        // back: customPlatingsIsActive['back'] ? depth : 0,
        // left: customPlatingsIsActive['left'] ? depth : 0,
        // right: customPlatingsIsActive['right'] ? depth : 0,
        front: depth,
        back: depth,
        left: depth,
        right: depth,
      },
      el.key === 'front' || el.key === 'back' ? monumentWidth : monumentLength,
    );

    let shapeDetails = {
      display: true,
      isRotated: el.isRotated,
      depth: depth,
      length: length,
      front: customSize?.heightFront,
      back: customSize?.heightBack,
      monumentWidth,
      monumentLength,
      monumentFront: customSize?.heightFront ?? 0.2,
      monumentBack: customSize?.heightBack,
      hasSlope: customSize?.hasSlope,
      orientation: orientation,
      mode,
    };

    if (customSize) {
      if (orientation === 'x' && customSize.heightBack !== undefined) {
        if (el.key === 'left') {
          shapeDetails.back = customSize.heightFront ?? 0.2;
        }
        if (el.key === 'right') {
          shapeDetails.front = customSize.heightBack === undefined ? 0.2 : customSize.heightBack;
        }
      }
      if (orientation === 'z' && customSize.heightBack !== undefined) {
        if (el.key === 'front') {
          shapeDetails.back = customSize.heightFront ?? 0.2;
        }
        if (el.key === 'back') {
          shapeDetails.front = customSize.heightBack === undefined ? 0.2 : customSize.heightBack;
        }
      }
    }

    if (shapeDetails.front === undefined) shapeDetails.front = 0.2;

    changePlatingShape(shape, length, shapeDetails.front, shapeDetails.back);

    platings.push({
      texture: customPlatings[elIndex]?.texture,
      granit: customPlatings[elIndex]?.granit,
      id: generateUUID(),
      key: el.key,
      shape,
      shapeDetails,
      isActive: customPlatings[elIndex] ? customPlatings[elIndex].isActive : true,
      position: getPositionWithIndex(
        el.key as 'front' | 'back' | 'left' | 'right',
        {
          front: shapeDetails.depth,
          back: shapeDetails.depth,
          left: shapeDetails.depth,
          right: shapeDetails.depth,
        },
        pieces,
        customSoles,
        customPlatingsIsActive,
        monumentLength,
        monumentWidth,
        mode,
      ),
      extrudeSettings: {
        curveSegments: 12,
        steps: 2,
        depth: depth,
        bevelEnabled: false,
      },
    });
  });

  setCustomPlatings(platings);
};

export const isPlatingUse = (customPlatings: any[], key: 'front' | 'back' | 'left' | 'right') => {
  const plating = customPlatings.find((el) => el.key === key);

  if (!plating) return false;
  return plating.isActive;
};

export const changePlatingStatus = (
  key: 'front' | 'back' | 'left' | 'right',
  customPlatings: any[],
  setCustomPlatings: (customPlatings: any[]) => void,
) => {
  const mapedCustomPlatings = customPlatings.map((el) => {
    if (el.key === key) {
      return {
        ...el,
        isActive: !el.isActive,
      };
    }
    return el;
  });

  setCustomPlatings(mapedCustomPlatings);
};

export const getBoxOfPlating = (customPlating: any) => {
  const box1GeometryThree = new THREE.ExtrudeGeometry(
    customPlating.shape,
    customPlating.extrudeSettings,
  );
  // @ts-ignore
  const box1Geometry: number[] = box1GeometryThree.attributes.position.array;
  const box1Postion = customPlating.position;
  const box1Rotation = new Euler(0, customPlating.shapeDetails.isRotated ? 1.57 : 0, 0, 'XYZ');

  const rotationMatrix = new THREE.Matrix4();
  rotationMatrix.makeRotationFromEuler(box1Rotation);

  const translationMatrix = new THREE.Matrix4();
  translationMatrix.makeTranslation(box1Postion.x, box1Postion.y, box1Postion.z);

  const geometryWithoutPosition = box1Geometry.slice();
  for (let i = 0; i < geometryWithoutPosition.length; i += 3) {
    const vertex = new THREE.Vector3(
      geometryWithoutPosition[i],
      geometryWithoutPosition[i + 1],
      geometryWithoutPosition[i + 2],
    );

    vertex.applyMatrix4(rotationMatrix);

    geometryWithoutPosition[i] = vertex.x;
    geometryWithoutPosition[i + 1] = vertex.y;
    geometryWithoutPosition[i + 2] = vertex.z;
  }

  const transformedGeometry = box1Geometry.slice();
  for (let i = 0; i < transformedGeometry.length; i += 3) {
    const vertex = new THREE.Vector3(
      transformedGeometry[i],
      transformedGeometry[i + 1],
      transformedGeometry[i + 2],
    );

    vertex.applyMatrix4(rotationMatrix);
    vertex.applyMatrix4(translationMatrix);

    transformedGeometry[i] = vertex.x;
    transformedGeometry[i + 1] = vertex.y;
    transformedGeometry[i + 2] = vertex.z;
  }

  const box = new THREE.Box3();
  box.setFromArray(transformedGeometry);

  return { box: box, geometry: transformedGeometry, geometryWithoutPosition };
};
